import type { CustomFieldSchema } from '@tribeplatform/gql-client/types'
import type { SvgIconName } from '@tribeplatform/react-ui-kit/Icon'

import type { I18nShape } from '../../i18n/types.js'

import {
  initialCheckboxField,
  initialDateField,
  initialDateTimeField,
  initialEmailField,
  initialFileField,
  initialFilesField,
  initialImageField,
  initialImagesField,
  initialMemberField,
  initialMembersField,
  initialNumberField,
  initialOptionField,
  initialOptionsField,
  initialRichtextField,
  initialSpaceField,
  initialSpacesField,
  initialTextField,
  initialTextareaField,
  initialToggleField,
  initialUrlField,
} from './initialFieldSchemas.js'

interface FieldOptionType {
  id: string
  name: string
  iconName: SvgIconName
  initialField: CustomFieldSchema
}
export const singleFieldOptionsFactory: (
  intl: I18nShape,
) => FieldOptionType[] = ({ $t }: I18nShape) => {
  const fieldOptions: FieldOptionType[] = [
    {
      id: initialTextField.key,
      name: $t({
        defaultMessage: 'Text',
        id: 'Admin.CMS.FieldOptions.Text',
      }),
      iconName: 'field-text',
      initialField: initialTextField,
    },
    {
      id: initialUrlField.key,
      name: $t({
        defaultMessage: 'URL',
        id: 'Admin.CMS.FieldOptions.Url',
      }),
      iconName: 'link',
      initialField: initialUrlField,
    },
    {
      id: initialEmailField.key,
      name: $t({
        defaultMessage: 'Email',
        id: 'Admin.CMS.FieldOptions.Email',
      }),
      iconName: 'at-symbol',
      initialField: initialEmailField,
    },
    {
      id: initialNumberField.key,
      name: $t({
        defaultMessage: 'Number',
        id: 'Admin.CMS.FieldOptions.Number',
      }),
      iconName: 'hash',
      initialField: initialNumberField,
    },
    {
      id: initialTextareaField.key,
      name: $t({
        defaultMessage: 'Textarea',
        id: 'Admin.CMS.FieldOptions.Textarea',
      }),
      iconName: 'field-text',
      initialField: initialTextareaField,
    },
    {
      id: initialRichtextField.key,
      name: $t({
        defaultMessage: 'Rich text',
        id: 'Admin.CMS.FieldOptions.RichText',
      }),
      iconName: 'field-text',
      initialField: initialRichtextField,
    },
    {
      id: initialOptionField.key,
      name: $t({
        defaultMessage: 'Option',
        id: 'Admin.CMS.FieldOptions.Option',
      }),
      iconName: 'selector',
      initialField: initialOptionField,
    },
    {
      id: initialDateField.key,
      name: $t({
        defaultMessage: 'Date',
        id: 'Admin.CMS.FieldOptions.Date.Name',
      }),
      iconName: 'calendar',
      initialField: initialDateField,
    },
    {
      id: initialDateTimeField.key,
      name: $t({
        defaultMessage: 'Date time',
        id: 'Admin.CMS.FieldOptions.DateTime',
      }),
      iconName: 'clock',
      initialField: initialDateTimeField,
    },
    {
      id: initialToggleField.key,
      name: $t({
        defaultMessage: 'Toggle',
        id: 'Admin.CMS.FieldOptions.Toggle',
      }),
      iconName: 'status-success',
      initialField: initialToggleField,
    },
    {
      id: initialCheckboxField.key,
      name: $t({
        defaultMessage: 'Checkbox',
        id: 'Admin.CMS.FieldOptions.Checkbox',
      }),
      iconName: 'check',
      initialField: initialCheckboxField,
    },
    {
      id: initialImageField.key,
      name: $t({
        defaultMessage: 'Image',
        id: 'Admin.CMS.FieldOptions.Image',
      }),
      iconName: 'image',
      initialField: initialImageField,
    },
    {
      id: initialFileField.key,
      name: $t({
        defaultMessage: 'File',
        id: 'Admin.CMS.FieldOptions.File',
      }),
      iconName: 'attachment',
      initialField: initialFileField,
    },
    {
      id: initialMemberField.key,
      name: $t({
        defaultMessage: 'Member',
        id: 'Admin.CMS.FieldOptions.Member',
      }),
      iconName: 'member',
      initialField: initialMemberField,
    },
    {
      id: initialSpaceField.key,
      name: $t({
        defaultMessage: 'Space',
        id: 'Admin.CMS.FieldOptions.Space',
      }),
      iconName: 'space',
      initialField: initialSpaceField,
    },
  ]

  return fieldOptions
}

export const multipleFieldOptionsFactory: (
  intl: I18nShape,
) => FieldOptionType[] = ({ $t }: I18nShape) => {
  const fieldOptions: FieldOptionType[] = [
    {
      id: initialOptionsField.key,
      name: $t({
        defaultMessage: 'Options',
        id: 'Admin.CMS.FieldOptions.Options',
      }),
      iconName: 'selector',
      initialField: initialOptionsField,
    },
    {
      id: initialImagesField.key,
      name: $t({
        defaultMessage: 'Images',
        id: 'Admin.CMS.FieldOptions.Images',
      }),
      iconName: 'image',
      initialField: initialImagesField,
    },
    {
      id: initialFilesField.key,
      name: $t({
        defaultMessage: 'Files',
        id: 'Admin.CMS.FieldOptions.Files',
      }),
      iconName: 'attachment',
      initialField: initialFilesField,
    },
    {
      id: initialMembersField.key,
      name: $t({
        defaultMessage: 'Members',
        id: 'Admin.CMS.FieldOptions.Members',
      }),
      iconName: 'members',
      initialField: initialMembersField,
    },
    {
      id: initialSpacesField.key,
      name: $t({
        defaultMessage: 'Spaces',
        id: 'Admin.CMS.FieldOptions.Spaces',
      }),
      iconName: 'space',
      initialField: initialSpacesField,
    },
  ]

  return fieldOptions
}
